import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";

import * as Mui from "@mui/material";
import { KeyboardArrowRightRounded } from "@mui/icons-material";

import CustomModal from "@components/CustomModal";

import Parser from "@helpers/parser";

import "./styles.css";

export default function GiveUserAccessToSubscription({ modalRef }) {
    const { t } = useTranslation("member");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [plansAvailable, setPlansAvailable] = useState([]);

    const REF_config = useRef({});
    const REF_callback = useRef();

    modalRef.current = {
        open(user, community, callback = () => { }) {
            REF_config.current = { user, community };
            REF_callback.current = callback;
            setOpen(true);
            setLoading(true);
            loadPlans(community);
        },
    };

    async function loadPlans(communityId) {
        try {
            const url = `community/${communityId}/plan`;
            const { data } = await api.get(url);
            setPlansAvailable(data || []);
        } catch (e) {
            console.error("Erro ao carregar planos:", e);
            window.setSnackbar(t("giveAccess.loadPlansError"));
        } finally {
            setLoading(false);
        }
    }

    function handlePlanSelect(plan) {
        const intervalDays = {
            week: 7,
            month: 30,
            bimonthly: 60,
            quarterly: 90,
            semiannual: 180,
            year: 365,
        };

        const daysToAdd = intervalDays[plan.interval] || 30;
        const timeLabel =
            plan.interval === 'lifetime'
                ? t("giveAccess.lifetime")
                : t("giveAccess.forDays", { days: daysToAdd })

        window.openConfirm({
            title: t("giveAccess.confirm.title"),
            headline: t("giveAccess.confirm.headline"),
            subtitle: t("giveAccess.confirm.subtitle", { plan: plan.label, time: timeLabel }),
            buttons: [
                {
                    text: t("common:actions.cancel"),
                    variant: "text",
                    onClick: () => { },
                },
                {
                    text: t("giveAccess.confirm.confirmButton"),
                    variant: "contained",
                    color: "primary",
                    onClick: async () => {
                        try {
                            await api.post(`community/${REF_config.current.community}/subscription/enable`, {
                                plan: plan.id,
                                user: REF_config.current.user.id,
                            });

                            window.setSnackbar(t("giveAccess.enabled"));
                            setOpen(false);
                            REF_callback.current();
                        } catch (error) {
                            console.error("Erro ao habilitar assinatura:", error);
                            window.setSnackbar(t("giveAccess.enableError"));
                        }
                    },
                },
            ],
        });
    }

    function PlanItem({ plan }) {
        return (
            <div
                className="plan-item pressable"
                onClick={() => handlePlanSelect(plan)}
            >
                <div className="give-access-plan-info-row">
                    <span className="plan-label bold">{plan.label}</span>
                    <span className="plan-label">• {Parser.interval.adjective(plan.interval)}, (R${Parser.price.brl(plan.price)})</span>
                </div>
                <KeyboardArrowRightRounded />
            </div>
        );
    }

    function PlanLoading() {
        return (
            <div className="plan-item" style={{ marginTop: "8px" }}>
                <Mui.Skeleton variant="rectangular" width="100%" height={60} />
            </div>
        );
    }

    return (
        <CustomModal
            maxWidth={350}
            bottomButtonCancelText={t("common:actions.close")}
            loading={loading}
            title={t("giveAccess.title")}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open ? (
                    <div className="px-4 py-2">
                        {loading ? (
                            <>
                                {["", "", ""].map((_, i) => (
                                    <PlanLoading key={i} />
                                ))}
                            </>
                        ) : (
                            <>
                                <Mui.Alert severity="warning" className="mb-3">
                                    {t("giveAccess.warning")}
                                </Mui.Alert>

                                <Mui.Divider className="plan-item-divider" />

                                <h4 className="mb-2 bold">{t("giveAccess.availablePlans")}</h4>

                                {plansAvailable.length > 0 ? (
                                    plansAvailable.map((plan, i) => (
                                        <>
                                            {i !== 0 && (
                                                <Mui.Divider className="plan-item-divider" />
                                            )}
                                            <PlanItem key={plan.id || i} plan={plan} />
                                        </>
                                    ))
                                ) : (
                                    <h4 className="center">{t("giveAccess.noPlans")}</h4>
                                )}
                            </>
                        )}
                    </div>
                ) : null
            }
        />
    );
}
